/* eslint-disable max-lines */
import { Form, Select, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { PlusButton } from 'src/components/common/PlusButton/PlusButton';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { getApplicationStatuses } from 'src/pages/LoanApplicationsPage/ApplicationStatusInfoMap';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import styled from 'styled-components';

const store = LoanApplicationsStore;

export const LoanApplicationsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <StyledHeaderWrapper>
            <SearchPanel
                store={store}
                renderPanel={(props) => <FilterPanel {...props} />}
                placeholder={t(
                    'Page.LoanApplication.AsideHeader.Search.Placeholder',
                )}
            />
            {UserStore.can.manage.applications && (
                <Tooltip
                    title={t(
                        'Page.LoanApplication.AsideHeader.CreateNewItem.Tooltip',
                    )}
                >
                    <PlusButton
                        onClick={() => {
                            const createLink =
                                EntityLinks.applications.process?.(
                                    createApplicationProcessName,
                                );
                            createLink && Router.navigate(createLink);
                        }}
                    />
                </Tooltip>
            )}
        </StyledHeaderWrapper>
    );
});

const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getApplicationStatuses();
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            initialValues={{ issuedAt: [] }}
            onFinish={(values) => {
                const { issuedAt, ...other } = values;
                store.pager.reset();
                const [issueDateFrom, issueDateTo] = issuedAt || [
                    undefined,
                    undefined,
                ];
                issueDateFrom?.set('hour', 0).set('minute', 0).set('second', 0);
                issueDateTo
                    ?.set('hour', 23)
                    .set('minute', 59)
                    .set('second', 59);
                store.filterCriteria?.applyFilter({
                    ...other,
                    issueDateFrom,
                    issueDateTo,
                });
                store.loadList();
            }}
        >
            <StyledSelect
                label={t('Page.LoanApplication.Filter.Status')}
                name="status"
            >
                <Select mode="multiple" allowClear>
                    {statuses.map((item) => (
                        <Select.Option value={item.id} key={item.id}>
                            {t(`data:status.application.${item.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledSelect>
            <Form.Item
                label={t('Page.LoanApplication.Filter.IssuedAt')}
                name="issuedAt"
            >
                <DateRangePicker />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const createApplicationProcessName = 'SendApplicationFromBackofficeProcess';

const StyledHeaderWrapper = styled.header`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
`;

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: 2.7rem;
        border-radius: 0.5rem;
    }
`;
